import { STATE_VERSION, type SimState } from '@orb/sim';
import { ProtocolError, utf8Decode, utf8Encode } from './codec.ts';
import { SNAPSHOT_CHUNK } from './constants.ts';

/** Largest snapshot accepted when reassembling (a full late-game state is well under this). */
export const MAX_SNAPSHOT_BYTES = 1 << 20;

/** The sim state as bytes: JSON (the state is JSON-safe by contract; its checksum ignores key order). */
export function encodeState(state: SimState): Uint8Array { return utf8Encode(JSON.stringify(state)); }

/**
 * Parse a received state. Only the envelope is checked here; whether the content is honest is
 * decided by comparing checksums with the other clients and the server's own replay (stages 7–8).
 */
export function decodeState(bytes: Uint8Array): SimState {
  let v: unknown;
  try { v = JSON.parse(utf8Decode(bytes)); } catch (e) { throw e instanceof ProtocolError ? e : new ProtocolError('snapshot is not JSON'); }
  const s = v as Partial<SimState> | null;
  if (!s || typeof s !== 'object' || Array.isArray(s)) throw new ProtocolError('snapshot is not an object');
  if (s.version !== STATE_VERSION) throw new ProtocolError(`snapshot state version ${String(s.version)}`);
  if (s.mode !== 'solo' && s.mode !== 'coop') throw new ProtocolError('snapshot mode');
  if (!Number.isInteger(s.tick) || !Array.isArray(s.players) || !Array.isArray(s.enemies)) throw new ProtocolError('snapshot shape');
  return s as SimState;
}

/** Split a payload into `snapshot` message parts that fit the server's frame limit. */
export function snapshotParts(tick: number, payload: Uint8Array, chunk = SNAPSHOT_CHUNK) {
  const total = Math.max(1, Math.ceil(payload.length / chunk));
  if (total > 65535) throw new ProtocolError('snapshot too large');
  return Array.from({ length: total }, (_, part) => ({ type: 'snapshot' as const, tick, part, total, data: payload.subarray(part * chunk, (part + 1) * chunk) }));
}

/** Collects snapshot parts for one tick; `add` returns the whole payload once every part arrived. */
export class SnapshotAssembler {
  private tick = -1;
  private parts: (Uint8Array | null)[] = [];
  private bytes = 0;

  add(m: { tick: number; part: number; total: number; data: Uint8Array }): Uint8Array | null {
    // a part for another tick starts over (a newer request replaced the old one)
    if (m.tick !== this.tick || m.total !== this.parts.length) { this.tick = m.tick; this.parts = new Array(m.total).fill(null); this.bytes = 0; }
    if (m.part >= m.total || this.parts[m.part]) throw new ProtocolError('bad or repeated snapshot part');
    this.bytes += m.data.length;
    if (this.bytes > MAX_SNAPSHOT_BYTES) { this.reset(); throw new ProtocolError('snapshot too large'); }
    this.parts[m.part] = m.data;
    if (this.parts.some((p) => !p)) return null;
    const out = new Uint8Array(this.bytes);
    let at = 0;
    for (const p of this.parts) { out.set(p!, at); at += p!.length; }
    this.reset();
    return out;
  }

  reset() { this.tick = -1; this.parts = []; this.bytes = 0; }
}
